const STILL = matchMedia('(prefers-reduced-motion: reduce)');

/* One <img> per frame, stacked. Showing a frame is a class toggle, not a
   network/decode round trip, so every step gets identical screen time. */
function buildFrames(host, count, src) {
  const imgs = Array.from({ length: count }, (_, k) => {
    const im = new Image();
    im.className = 'frame';
    im.src = src(k + 1);
    im.alt = '';
    host.appendChild(im);
    return im;
  });
  return {
    show:  n => imgs.forEach((im, k) => im.classList.toggle('on', k === n - 1)),
    clear: () => imgs.forEach(im => im.classList.remove('on'))
  };
}

/* Ping-pong loop, shared by the flower and the butterfly */
function sprite(id, count, src, frameMs, vary = 0) {
  const host = document.getElementById(id);
  if (!host) return;

  // Out and back: 1->count, then count-1->2. The end frames aren't repeated,
  // so the loop rejoins itself without pausing at either end of the swing.
  const up  = Array.from({ length: count }, (_, i) => i + 1);
  const seq = [...up, ...up.slice(1, -1).reverse()];

  const { show } = buildFrames(host, count, src);

  if (STILL.matches) {
    show(Math.ceil(count / 2));     // rest on the middle frame
    return;
  }

  let i = 0, t = 0;
  show(seq[i]);

  // Self-scheduling rather than setInterval, so the gap between frames can
  // drift. Two slow waves at unrelated periods means the ebb and flow never
  // settles into an obvious pattern - it just wanders a little.
  const tick = () => {
    i = (i + 1) % seq.length;
    show(seq[i]);

    let delay = frameMs;
    if (vary) {
      t++;
      const drift = Math.sin(t / 23) * .62 + Math.sin(t / 7.3) * .38;
      delay *= 1 + drift * vary;
    }
    setTimeout(tick, delay);
  };
  setTimeout(tick, frameMs);
}

/* One-shot burst: plays 1->count once, then clears itself */
function burst(id, count, src, frameMs) {
  const host = document.getElementById(id);
  if (!host) return () => {};

  const { show, clear } = buildFrames(host, count, src);
  let playing = false;

  return () => {
    if (STILL.matches || playing) return;   // ignore clicks until this one ends
    playing = true;
    host.classList.add('playing');

    let n = 1;
    const step = () => {
      show(n);
      if (n++ < count) {
        setTimeout(step, frameMs);
      } else {
        setTimeout(() => {          // let the last frame hold, then vanish
          clear();
          playing = false;
          host.classList.remove('playing');
        }, frameMs);
      }
    };
    step();
  };
}

/* Shopify serves assets off its CDN with a cache-busting query, so filenames
   can't be built by hand. theme.liquid emits a name -> URL map; fall back to
   the bare filename so this file still works opened straight from disk. */
const asset = name => (window.VT_ASSETS && window.VT_ASSETS[name]) || name;

const pop = burst('bloomFx', 14, n => asset(`fl${n}.png`), 55);
document.getElementById('flower')?.addEventListener('click', pop);

sprite('flower', 13, n => asset(`f${n}.png`), 130);
sprite('butterfly', 13, n => asset(`butterfly_${String(n).padStart(2, '0')}.png`), 190, .3);

/* Terminal nav: a caret that travels, labels that decode */
const CAN_HOVER = matchMedia('(hover: hover)');

/* Desktop is drawn at 90% with CSS zoom (see viprtech.css). getBoundingClientRect
   reports pixels that are already scaled by it, but anything written back - a
   CSS length, a scrollLeft, a comparison against innerWidth - is in unscaled
   layout pixels. Mixing the two makes a measured position land short.

   Read straight off the root rather than inferred from an element's own
   measurements. Deriving it as rect.width / offsetWidth looked equivalent and
   is not: a rect includes transforms, so measuring a panel mid-entrance
   returned the zoom multiplied by whatever that animation was doing, and the
   panel was placed against a factor that changed frame to frame.

   Returns 1 wherever no zoom applies - every screen under 1100px - so none of
   this changes the phone or tablet behaviour. */
function layoutScale() {
  const z = parseFloat(getComputedStyle(document.documentElement).zoom);
  return z > 0 ? z : 1;
}

/* Phone menu
   The button is display: none above the breakpoint, so none of this runs on
   desktop. */
(() => {
  const btn = document.getElementById('navToggle');
  const nav = document.getElementById('mobileNav');
  if (!btn || !nav) return;

  const isOpen = () => nav.classList.contains('is-open');

  function close() {
    nav.classList.remove('is-open');
    btn.setAttribute('aria-expanded', 'false');
  }
  function open() {
    nav.classList.add('is-open');
    btn.setAttribute('aria-expanded', 'true');
  }

  btn.addEventListener('click', e => {
    e.stopPropagation();
    isOpen() ? close() : open();
  });

  // a link leaves the menu behind rather than open over the new page
  nav.addEventListener('click', e => {
    if (e.target.closest('a')) close();
  });

  document.addEventListener('click', e => {
    if (isOpen() && !nav.contains(e.target) && e.target !== btn) close();
  });

  addEventListener('keydown', e => {
    if (e.key === 'Escape' && isOpen()) { close(); btn.focus(); }
  });

  // rotating can cross the breakpoint, where the panel styles stop applying
  addEventListener('resize', () => { if (isOpen()) close(); });
})();

(() => {
  const CHARS = '!<>-_\\/[]{}—=+*^?#';

  const nav   = document.querySelector('.nav');
  const caret = nav && nav.querySelector('.caret');
  if (!nav || !caret) return;       // no nav on this page - nothing to drive

  const items = [...nav.querySelectorAll('.nav-item')];
  if (!items.length) return;
  // The caret's resting place is whichever item matches the current page,
  // marked by Liquid with data-current. Falls back to the first item.
  const home = items.find(i => i.hasAttribute('data-current')) || items[0];
  let current = home;

  /* Measured with offsets, not a client rect.

     The caret is absolutely positioned inside .nav, so its left/width/transform
     are read in .nav's own layout coordinates. offsetLeft and friends are in
     exactly that space and ignore CSS zoom entirely, while a client rect is
     reported in scaled screen pixels - feeding one into the other is what put
     the highlight in the wrong place at 90%.

     Offsets also need no scroll correction: .nav scrolls sideways on narrow
     screens, and offsetLeft is already relative to the unscrolled content,
     which is the same thing the caret's transform is measured against. */
  const box = el => {
    let x = 0, y = 0;
    for (let node = el; node && node !== nav; node = node.offsetParent) {
      x += node.offsetLeft;
      y += node.offsetTop;
    }
    return { x: x, y: y, w: el.offsetWidth, h: el.offsetHeight };
  };

  function park(el, animate = true) {
    const b = box(el);
    if (!animate) caret.style.transition = 'none';
    caret.style.top    = b.y + 'px';
    caret.style.height = b.h + 'px';
    caret.style.width  = b.w + 'px';
    caret.style.transform = `translateX(${b.x}px)`;
    if (!animate) { caret.offsetHeight; caret.style.transition = ''; }

    items.forEach(i => i.classList.toggle('lit', i === el));

    // The cursor only exists at rest - it stays hidden for the whole trip,
    // then blinks on once the caret has landed (and never on Support).
    clearTimeout(caret._c);
    caret.classList.add('bare');
    // Any item with a dropdown ends in a chevron, which the cursor collides with
    if (!el.hasAttribute('aria-haspopup')) {
      if (animate) caret._c = setTimeout(() => caret.classList.remove('bare'), 360);
      else caret.classList.remove('bare');
    }

    current = el;
  }

  function moveTo(el) {
    if (el === current) return;
    park(el);
    decode(el.querySelector('.lbl'));
  }

  /* Resolve the word left to right out of noise. Spaces are left alone so
     the shape of the label stays readable the whole way through. */
  function decode(lbl) {
    const word = lbl.dataset.text;
    clearInterval(lbl._d);
    let frame = 0;
    lbl._d = setInterval(() => {
      lbl.textContent = word.split('').map((c, i) =>
        (c === ' ' || i < frame / 2) ? c : CHARS[Math.random() * CHARS.length | 0]
      ).join('');
      if (frame++ > word.length * 2) {
        clearInterval(lbl._d);
        lbl.textContent = word;
      }
    }, 26);
  }

  items.forEach(it => {
    it.querySelector('.lbl').dataset.text = it.querySelector('.lbl').textContent;
    // touch fires mouseenter on tap, decoding a label on the way out
    it.addEventListener('mouseenter', () => { if (CAN_HOVER.matches) moveTo(it); });
    it.addEventListener('focus', () => moveTo(it));
  });

  // The panel's links borrow the same decode
  document.querySelectorAll('.cta').forEach(cta => {
    cta.dataset.text = cta.textContent.trim();
    cta.textContent = cta.dataset.text;
    cta.addEventListener('mouseenter', () => decode(cta));
    cta.addEventListener('focus', () => decode(cta));
  });

  // Leaving the row sends the caret home - unless a dropdown is still open
  nav.addEventListener('mouseleave', () => {
    const open = items.find(i => i.getAttribute('aria-expanded') === 'true');
    moveTo(open || home);
  });

  // Wait for the bar to finish unfolding before placing the caret
  setTimeout(() => park(home, false), 1080);
  addEventListener('resize', () => park(current, false));
})();

/* Dropdowns
   One per nav item that has child links, so the menu structure comes from
   Shopify navigation rather than being baked into the markup. */
document.querySelectorAll('[data-dropdown]').forEach(btn => {
  const menu = document.getElementById(btn.dataset.dropdown);
  if (!menu) return;
  let closeTimer = null;

  // Anchor the menu's right edge to the button's right edge
  function place() {
    const z = layoutScale();
    const r = btn.getBoundingClientRect();
    menu.style.top   = (r.bottom / z + 10) + 'px';
    menu.style.left  = Math.max(12, r.right / z - menu.offsetWidth) + 'px';
  }

  function open() {
    clearTimeout(closeTimer);
    place();
    menu.classList.add('open');
    btn.setAttribute('aria-expanded', 'true');
  }

  function close() {
    menu.classList.remove('open');
    btn.setAttribute('aria-expanded', 'false');
  }

  const isOpen = () => menu.classList.contains('open');

  btn.addEventListener('click', e => { e.stopPropagation(); isOpen() ? close() : open(); });

  // Hover to open, with a grace period so the diagonal trip to the menu is survivable
  [btn, menu].forEach(el => {
    el.addEventListener('mouseenter', () => { if (CAN_HOVER.matches) open(); });
    el.addEventListener('mouseleave', () => { closeTimer = setTimeout(close, 220); });
  });

  document.addEventListener('click', e => {
    if (isOpen() && !menu.contains(e.target) && e.target !== btn) close();
  });

  addEventListener('keydown', e => {
    if (e.key === 'Escape' && isOpen()) { close(); btn.focus(); }
  });

  addEventListener('resize', () => { if (isOpen()) place(); });
});

/* product carousel */
(() => {
  // The dwell is set by the `dwell` keyframe's 3s duration in the CSS -
  // that animation is the clock now, so there's no interval to keep in step.
  const showcase = document.getElementById('showcase');
  const track    = document.getElementById('track');
  const dotsWrap = document.getElementById('dots');
  if (!showcase || !track || !dotsWrap) return;   // carousel is home page only

  const slides = [...track.children];
  if (!slides.length) return;

  let index = 0, settling = false;

  // Build one dot per slide
  const dots = slides.map((slide, n) => {
    const b = document.createElement('button');
    b.className = 'dot';
    b.type = 'button';
    b.setAttribute('role', 'tab');
    const name = slide.querySelector('h2').textContent;
    b.setAttribute('aria-label', name);
    b.innerHTML = '<i></i>';
    b.appendChild(document.createTextNode(name));
    b.addEventListener('click', () => { go(n); hold(); });
    dotsWrap.appendChild(b);
    return b;
  });

  function paint() {
    slides.forEach((s, n) => s.classList.toggle('is-active', n === index));
    dots.forEach((d, n) => d.setAttribute('aria-current', String(n === index)));

    // Restart the fill from zero. Re-adding the attribute isn't enough when
    // the same dot stays active, so the animation is forced to re-run.
    const fill = dots[index].querySelector('i');
    fill.style.animation = 'none';
    void fill.offsetWidth;              // flush, so the next line re-triggers it
    fill.style.animation = '';
  }

  function go(n) {
    index = (n + slides.length) % slides.length;
    // Flag the scroll as ours so the scroll handler ignores the frames in between
    settling = true;
    clearTimeout(go._t);
    go._t = setTimeout(() => settling = false, 700);

    track.scrollLeft = track.clientWidth * index;
    paint();
  }

  // The fill bar *is* the timer: when it finishes, we advance. Nothing can
  // drift out of sync with it, and pausing the animation pauses the carousel.
  dotsWrap.addEventListener('animationend', e => {
    if (e.animationName === 'dwell') go(index + 1);
  });

  // Keep state honest when the user scrolls the track themselves. Bailing on
  // `settling` matters: mid-flight scroll positions still round to the old
  // index, which would drag `index` backwards and restart the fill forever.
  track.addEventListener('scroll', () => {
    if (settling) return;
    const n = Math.round(track.scrollLeft / track.clientWidth);
    if (n !== index && !Number.isNaN(n)) { index = n; paint(); }
  }, { passive: true });

  showcase.querySelector('.prev').addEventListener('click', () => go(index - 1));
  showcase.querySelector('.next').addEventListener('click', () => go(index + 1));

  // Hovering to look at a build shouldn't yank it away - freezing the fill
  // freezes the rotation, since the fill is what drives it.
  const pause = on => showcase.classList.toggle('is-paused', on);
  ['mouseenter', 'focusin'].forEach(e => showcase.addEventListener(e, () => pause(true)));
  ['mouseleave', 'focusout'].forEach(e => showcase.addEventListener(e, () => pause(false)));

  // Arrow keys
  addEventListener('keydown', e => {
    if (e.key === 'ArrowRight') go(index + 1);
    if (e.key === 'ArrowLeft')  go(index - 1);
  });

  // Don't run against a tab nobody is looking at
  document.addEventListener('visibilitychange', () => pause(document.hidden));

  // Re-anchor if the window resizes mid-slide
  addEventListener('resize', () => { track.scrollLeft = track.clientWidth * index; });

  paint();
})();
/* Collection cards: click a finish to swap that card's thumbnail
   Purely local. It rewrites the <img> in the DOM and nothing else: no
   navigation, no form, no request to Shopify, nothing persisted. Reload the
   page and every card is back to its featured image. */
document.querySelectorAll('.build').forEach(card => {
  const img = card.querySelector('.build__media img');
  const swatches = [...card.querySelectorAll('.build__swatch')];
  if (!img || !swatches.length) return;

  swatches.forEach(btn => {
    btn.addEventListener('click', () => {
      const src = btn.dataset.img;
      if (!src) return;

      // srcset wins over src, so it has to go or the swap is ignored.
      // sizes too, since it only means anything alongside srcset.
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = src;

      swatches.forEach(b =>
        b.setAttribute('aria-pressed', String(b === btn)));
    });
  });
});

/* Product page: quantity stepper
   The input is a real number field, so typing and form submission work with
   the JS off; these buttons are just a nicer way to nudge it. */
(() => {
  const input = document.getElementById('prodQty');
  if (!input) return;

  // max is set from the variant's stock, so the picker can't exceed it.
  // Absent (untracked / backorder-friendly variants) means no ceiling.
  const ceiling = () => {
    const max = parseInt(input.max, 10);
    return Number.isFinite(max) && max > 0 ? max : Infinity;
  };

  document.querySelectorAll('.qty__step').forEach(btn => {
    btn.addEventListener('click', () => {
      const step = parseInt(btn.dataset.step, 10);
      const min = parseInt(input.min, 10) || 1;
      const next = (parseInt(input.value, 10) || min) + step;
      input.value = Math.min(ceiling(), Math.max(min, next));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });

  // typing straight into the box has to obey the same ceiling
  input.addEventListener('change', () => {
    const min = parseInt(input.min, 10) || 1;
    const typed = parseInt(input.value, 10);
    if (!Number.isFinite(typed)) { input.value = min; return; }
    input.value = Math.min(ceiling(), Math.max(min, typed));
  });
})();

/* Product gallery: click a photo to open it in a viewer
   A panel, not a full-screen takeover: it covers most of the screen so the
   photo is genuinely bigger, but stays a window with the page dimmed behind.
   Inside, clicking the image magnifies further and pans with the pointer. */
(() => {
  const main   = document.getElementById("galleryMain");
  const viewer = document.getElementById("viewer");
  if (!viewer) return;

  const stage = document.getElementById("viewerStage");
  const img   = document.getElementById("viewerImg");
  const count = document.getElementById("viewerCount");
  const navs  = viewer.querySelectorAll(".viewer__nav");

  let shots = [], at = 0, lastFocus = null;

  // Only the photos on screen - the colour filter hides the rest, and the
  // viewer should never surface one the customer cannot currently see.
  const visible = () => main ? [...main.children].filter(el => !el.hidden) : [];

  function unzoom() {
    stage.classList.remove("is-zoomed", "is-panning");
    img.style.transformOrigin = "";
  }

  function show(i) {
    at = (i + shots.length) % shots.length;
    const fig = shots[at];
    const src = fig.dataset.full || fig.querySelector("img").src;
    unzoom();
    img.src = src;
    img.alt = fig.querySelector("img").alt || "";
    count.textContent = shots.length > 1 ? (at + 1) + " / " + shots.length : "";
    navs.forEach(b => b.hidden = shots.length < 2);
  }

  function openList(list, i) {
    if (!list.length || i < 0) return;
    shots = list;
    lastFocus = document.activeElement;
    viewer.hidden = false;
    document.body.style.overflow = "hidden";   // stop the page scrolling behind
    show(i);
    viewer.querySelector(".viewer__close").focus();
  }

  function close() {
    unzoom();
    viewer.hidden = true;
    document.body.style.overflow = "";
    if (lastFocus) lastFocus.focus();
  }

  if (main) main.addEventListener("click", e => {
    const fig = e.target.closest(".shot");
    if (!fig) return;
    const list = visible();
    openList(list, list.indexOf(fig));
  });

  /* The case card photo opens the same viewer, on its own - it is not part of
     the product gallery, so it is passed as a one-item list. */
  const casePhoto = document.querySelector(".casecard__media");
  if (casePhoto) {
    const openCase = () => openList([casePhoto], 0);
    casePhoto.addEventListener("click", openCase);
    casePhoto.addEventListener("keydown", e => {
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openCase(); }
    });
  }

  viewer.addEventListener("click", e => {
    if (e.target === viewer) return close();            // the dimmed area
    if (e.target.closest(".viewer__close")) return close();
    const nav = e.target.closest(".viewer__nav");
    if (nav) return show(at + (nav.classList.contains("next") ? 1 : -1));
  });

  /* magnify inside the viewer */
  const aim = e => {
    const r = stage.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * 100;
    const y = ((e.clientY - r.top) / r.height) * 100;
    img.style.transformOrigin = x + "% " + y + "%";
  };

  stage.addEventListener("click", e => {
    if (stage.classList.toggle("is-zoomed")) aim(e);
    else unzoom();
  });

  stage.addEventListener("mousemove", e => {
    if (!stage.classList.contains("is-zoomed")) return;
    stage.classList.add("is-panning");   // no transition, so it tracks 1:1
    aim(e);
  });

  addEventListener("keydown", e => {
    if (viewer.hidden) return;
    if (e.key === "Escape")     close();
    if (e.key === "ArrowRight") show(at + 1);
    if (e.key === "ArrowLeft")  show(at - 1);
  });
})();

/* Cart page: "Remove" sets the qty to 0 and submits the line's own form
   Each line is its own <form action="/cart/change">, so this still works
   with JS off - the visible quantity field and its Update button are the
   fallback path. */
document.querySelectorAll(".line [data-remove]").forEach(btn => {
  btn.addEventListener("click", () => {
    const form = btn.closest("form");
    const qty = form && form.querySelector(".line__qty");
    if (!qty) return;
    qty.value = 0;
    form.submit();
  });
});

/* Cart page: a line can't be typed above what's in stock. max comes from the
   variant's inventory; without one there's no ceiling. */
document.querySelectorAll(".line__qty").forEach(input => {
  input.addEventListener("change", () => {
    const max = parseInt(input.max, 10);
    const typed = parseInt(input.value, 10);
    if (!Number.isFinite(typed) || typed < 0) { input.value = 0; return; }
    if (Number.isFinite(max) && max > 0 && typed > max) input.value = max;
  });
});

/* Cart preview
   Clicking the cart button shows what's in it rather than navigating; the
   href stays a real link, so with JS off it still just goes to /cart.
   Exposes VT_CART so the add-to-cart flow can refresh and open it. */
window.VT_CART = (() => {
  const btn  = document.querySelector('.cart-link');
  const peek = document.getElementById('cartPeek');
  if (!btn || !peek) return { refresh() {}, open() {} };

  /* Anchored to the button's right edge, then clamped into the viewport.
     The clamp matters: the bar can overflow horizontally on small screens,
     which puts the button partly off-screen and would drag the panel with
     it. Clamping keeps the panel reachable no matter where the button sits. */
  function place() {
    const z = layoutScale();
    const r = btn.getBoundingClientRect();
    const w = peek.offsetWidth;
    const view = window.innerWidth / z;
    const maxLeft = Math.max(12, view - w - 12);
    const left = Math.min(Math.max(12, r.right / z - w), maxLeft);
    peek.style.top  = (r.bottom / z + 12) + 'px';
    peek.style.left = left + 'px';
  }

  const isOpen = () => peek.classList.contains('open');
  function open()  { place(); peek.classList.add('open'); btn.setAttribute('aria-expanded', 'true'); }
  function close() { peek.classList.remove('open'); btn.setAttribute('aria-expanded', 'false'); }

  btn.addEventListener('click', e => {
    e.preventDefault();
    e.stopPropagation();
    isOpen() ? close() : open();
  });

  document.addEventListener('click', e => {
    if (isOpen() && !peek.contains(e.target) && !btn.contains(e.target)) close();
  });
  addEventListener('keydown', e => {
    if (e.key === 'Escape' && isOpen()) { close(); btn.focus(); }
  });
  addEventListener('resize', () => { if (isOpen()) place(); });

  /* Re-render from the server so the panel and the button's count stay true
     to the real cart, without duplicating the Liquid markup in JS. */
  function refresh() {
    return fetch(window.VT_ROUTES.section, { headers: { Accept: 'text/html' } })
      .then(res => res.text())
      .then(html => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const freshPeek = doc.getElementById('cartPeek');
        const freshBtn  = doc.querySelector('.cart-link');
        if (freshPeek) peek.innerHTML = freshPeek.innerHTML;
        if (freshBtn) {
          btn.classList.toggle('has-items', freshBtn.classList.contains('has-items'));
          const badge = btn.querySelector('.cart-count');
          const fresh = freshBtn.querySelector('.cart-count');
          if (fresh && badge) badge.textContent = fresh.textContent;
          else if (fresh && !badge) btn.appendChild(fresh.cloneNode(true));
          else if (!fresh && badge) badge.remove();
        }
      })
      .catch(() => {});
  }

  return { refresh, open, close, isOpen };
})();

/* Product page: add to cart without leaving the page
   Progressive enhancement over the form's real action/method: if the fetch
   fails for any reason, the catch re-enables the button and a normal click
   still posts the form the old way. */
(() => {
  const form  = document.getElementById("prodForm");
  const btn   = document.getElementById("prodAdd");
  const toast = document.getElementById("cartToast");
  if (!form || !btn || !toast || !window.VT_ROUTES) return;

  const toastItem  = toast.querySelector(".toast__item");
  const toastTitle = toast.querySelector(".toast__title");
  let toastTimer;

  function showToast(label, isError) {
    if (toastTitle) toastTitle.textContent = isError ? "Couldn't add that" : "Added to cart";
    if (toastItem) toastItem.textContent = label;
    toast.classList.toggle("is-error", !!isError);
    toast.classList.remove("is-visible");
    void toast.offsetWidth;             // restarts the checkmark draw-in on repeat adds
    toast.classList.add("is-visible");
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove("is-visible"), isError ? 5000 : 3400);
  }

  form.addEventListener("submit", e => {
    if (btn.disabled) return;
    e.preventDefault();

    btn.disabled = true;
    btn.classList.add("is-loading");

    fetch(window.VT_ROUTES.cartAdd, {
      method: "POST",
      headers: { Accept: "application/json" },
      body: new FormData(form)
    })
      .then(res => res.json())
      .then(data => {
        /* Shopify rejects an add that would push past stock, counting what's
           already in the cart - something the picker's max can't know. That
           rejection is the real ceiling, so its message gets shown rather
           than swallowed. */
        if (data.status) throw new Error(data.description || data.message || "Could not add to cart");

        showToast(data.product_title || document.querySelector(".prod__info h1")?.textContent || "Item");

        btn.classList.remove("is-loading");
        btn.classList.add("is-added");
        setTimeout(() => {
          btn.classList.remove("is-added");
          btn.disabled = false;
        }, 1600);

        // one server round trip keeps the badge and the preview panel in step
        return window.VT_CART.refresh();
      })
      .catch(err => {
        showToast(err.message || "Could not add to cart", true);
        btn.classList.remove("is-loading");
        btn.disabled = false;
      });
  });
})();

/* Load screen
   The overlay clears itself on a CSS timer either way; this just brings that
   forward once loading is done. FLOOR stops a cached page flashing it. */
(() => {
  const el = document.getElementById('pageLoad');
  if (!el) return;

  const FLOOR = 380;
  const start = performance.now();

  const dismiss = () => {
    const wait = Math.max(0, FLOOR - (performance.now() - start));
    setTimeout(() => el.classList.add('is-done'), wait);
  };

  if (document.readyState === 'complete') dismiss();
  else addEventListener('load', dismiss, { once: true });
})();

/* Flower placement (phones)
   It sits in the gap between the menu button and the wordmark. The wordmark
   is centred, so that gap moves with the screen width and has to be
   measured. CSS holds a rough fallback position. */
(() => {
  const layer  = document.querySelector('.bar-layer');
  const flower = document.getElementById('flower');
  const brand  = document.querySelector('.brand');
  if (!layer || !flower || !brand) return;

  const PHONE = matchMedia('(max-width: 860px)');

  function place() {
    if (!PHONE.matches) {                 // desktop keeps its own corner
      flower.style.right = '';
      return;
    }
    const l = layer.getBoundingClientRect();
    const b = brand.getBoundingClientRect();
    if (!b.width) return;

    const GAP = 36;                       // clear of the lettering
    const centre = b.left - GAP;          // the stem's top stays in CSS
    flower.style.right = (l.right - centre - flower.offsetWidth / 2) + 'px';
  }

  place();
  addEventListener('load', place);
  addEventListener('resize', place);
  PHONE.addEventListener?.('change', place);

  // the wordmark's width changes when its font finishes loading
  if ('ResizeObserver' in window) new ResizeObserver(place).observe(brand);
})();


/* Collection filter
   The chips are built by Liquid from whatever is in the collection, so this
   only has to read them. Filtering the cards already on the page keeps it
   instant and needs no app; a collection past one page would need the
   server-side version instead. */
(() => {
  const root = document.getElementById('collFilter');
  const grid = document.querySelector('.builds');
  if (!root || !grid) return;

  const toggle = document.getElementById('filterToggle');
  const panel  = document.getElementById('filterPanel');
  const count  = document.getElementById('filterCount');
  const result = document.getElementById('filterResult');
  const clear  = document.getElementById('filterClear');

  const chips = [...root.querySelectorAll('.chip')];
  const cards = [...grid.querySelectorAll('.build')];
  const rules = [...grid.querySelectorAll('.series-rule')];

  const empty = document.createElement('p');
  empty.className = 'builds__empty';
  empty.hidden = true;
  empty.textContent = 'Nothing matches those filters.';
  grid.appendChild(empty);

  const on = f => chips.filter(c => c.dataset.facet === f && c.getAttribute('aria-pressed') === 'true');

  function matches(card) {
    for (const facet of ['gpu', 'cpu', 'ram', 'storage', 'type']) {
      const picked = on(facet);
      if (picked.length && !picked.some(c => c.dataset.value === card.dataset[facet])) return false;
    }
    const stock = on('stock');
    if (stock.length && card.dataset.available !== '1') return false;

    const bands = on('price');
    if (bands.length) {
      const p = Number(card.dataset.price);
      if (!bands.some(b => p >= Number(b.dataset.min) && p <= Number(b.dataset.max))) return false;
    }
    return true;
  }

  function apply() {
    let shown = 0;
    cards.forEach(card => {
      const ok = matches(card);
      card.hidden = !ok;
      if (ok) shown++;
    });

    // The series rules divide groups that may now be empty, so they only
    // make sense while nothing is filtered.
    const active = chips.filter(c => c.getAttribute('aria-pressed') === 'true').length;
    rules.forEach(r => { r.hidden = active > 0; });

    empty.hidden = shown > 0;
    count.hidden = active === 0;
    count.textContent = active;
    clear.hidden = active === 0;
    result.textContent = active
      ? shown + (shown === 1 ? ' build' : ' builds')
      : '';

    // An option that can no longer be reached is dimmed rather than removed,
    // so the panel doesn't reshuffle under the pointer.
    chips.forEach(chip => {
      if (chip.getAttribute('aria-pressed') === 'true') { chip.disabled = false; return; }
      const was = chip.getAttribute('aria-pressed');
      chip.setAttribute('aria-pressed', 'true');
      chip.disabled = !cards.some(matches);
      chip.setAttribute('aria-pressed', was || 'false');
    });
  }

  chips.forEach(chip => {
    chip.setAttribute('aria-pressed', 'false');
    chip.addEventListener('click', () => {
      if (chip.disabled) return;
      chip.setAttribute('aria-pressed',
        chip.getAttribute('aria-pressed') === 'true' ? 'false' : 'true');
      apply();
    });
  });

  function setOpen(open) {
    panel.hidden = !open;
    toggle.setAttribute('aria-expanded', String(open));
  }

  toggle.addEventListener('click', e => {
    e.stopPropagation();
    setOpen(panel.hidden);
  });

  // it floats over the grid now, so it has to close like any other popover
  document.addEventListener('click', e => {
    if (!panel.hidden && !root.contains(e.target)) setOpen(false);
  });
  addEventListener('keydown', e => {
    if (e.key === 'Escape' && !panel.hidden) { setOpen(false); toggle.focus(); }
  });

  clear.addEventListener('click', () => {
    chips.forEach(c => c.setAttribute('aria-pressed', 'false'));
    apply();
  });

  apply();
})();


/* Collection sort
   Reorders the cards already on the page. "Collection order" is the order the
   template rendered them in, kept aside at load so it can be restored. */
(() => {
  const root = document.getElementById('collSort');
  const grid = document.querySelector('.builds');
  if (!root || !grid) return;

  const toggle = document.getElementById('sortToggle');
  const panel  = document.getElementById('sortPanel');
  const label  = document.getElementById('sortLabel');
  const opts   = [...root.querySelectorAll('.sort__opt')];

  const cards = [...grid.querySelectorAll('.build')];
  const rules = [...grid.querySelectorAll('.series-rule')];
  const original = cards.slice();               // the server's order

  const priceOf = c => Number(c.dataset.price) || 0;
  const nameOf = c => (c.querySelector('.build__name')?.textContent || '').trim().toLowerCase();

  const ORDERS = {
    'default':    null,
    'price-asc':  (a, b) => priceOf(a) - priceOf(b),
    'price-desc': (a, b) => priceOf(b) - priceOf(a),
    'az':         (a, b) => nameOf(a).localeCompare(nameOf(b))
  };

  function setOpen(open) {
    panel.hidden = !open;
    toggle.setAttribute('aria-expanded', String(open));
  }

  function sortBy(key) {
    const cmp = ORDERS[key];
    const list = cmp ? cards.slice().sort(cmp) : original;

    // One reflow rather than one per card.
    const frag = document.createDocumentFragment();
    list.forEach(c => frag.appendChild(c));
    grid.appendChild(frag);

    // The series rules divide the server's grouping, which no longer holds
    // once the cards are in a different order.
    rules.forEach(r => { r.hidden = key !== 'default'; });

    opts.forEach(o => {
      const on = o.dataset.sort === key;
      o.setAttribute('aria-checked', String(on));
      if (on) label.textContent = o.textContent.trim();
    });
  }

  opts.forEach(o => o.addEventListener('click', () => {
    sortBy(o.dataset.sort);
    setOpen(false);
  }));

  toggle.addEventListener('click', e => {
    e.stopPropagation();
    setOpen(panel.hidden);
  });

  document.addEventListener('click', e => {
    if (!panel.hidden && !root.contains(e.target)) setOpen(false);
  });
  addEventListener('keydown', e => {
    if (e.key === 'Escape' && !panel.hidden) { setOpen(false); toggle.focus(); }
  });
})();

/* Process band
   The four stages light in turn for as long as the band is on screen, so the
   section reads as a process running rather than four things sitting there.
   A mark leaves the live stage and lands on the next one; CSS draws it, this
   only decides whose turn it is.

   The walk stops the moment the band scrolls out and starts over when it
   comes back, so nothing is looping against a section nobody is looking at. */
(() => {
  const band = document.querySelector('.process');
  if (!band) return;

  const stages = band.querySelectorAll('.process__node');
  if (!stages.length) return;

  const BEAT = 1900;                    // how long a stage holds
  const LEAD = 1400;                    // lets the opening land first

  let lead = 0;
  let tick = 0;
  let at = 0;

  function light() {
    const i = at % stages.length;

    /* Top of a new pass: clear the trail without animating it back, or all
       four marks slide home at once and it reads as a mistake. */
    if (i === 0 && at > 0) {
      stages.forEach(s => s.classList.remove('is-live', 'is-done'));
      void band.offsetWidth;
    }

    stages.forEach((s, n) => {
      s.classList.toggle('is-live', n === i);
      s.classList.toggle('is-done', n < i);
    });
    at++;
  }

  function start() {
    if (STILL.matches || lead || tick) return;
    lead = setTimeout(() => {
      lead = 0;
      light();
      tick = setInterval(light, BEAT);
    }, LEAD);
  }

  function stop() {
    clearTimeout(lead);
    clearInterval(tick);
    lead = tick = at = 0;
    stages.forEach(s => s.classList.remove('is-live', 'is-done'));
  }

  new IntersectionObserver(entries => {
    entries.forEach(entry => {
      band.classList.toggle('is-in', entry.isIntersecting);
      if (entry.isIntersecting) start();
      else stop();
    });
  }, { rootMargin: '0px 0px -18% 0px' }).observe(band);
})();

/* Related products carousel (product pages)
   The track is a genuine scroller, so wheel, drag and swipe already work on
   their own. The arrows only move it by one card, and the step is measured
   off the cards themselves rather than hard-coded, so changing the card width
   or the gap in CSS needs no change here. */
(() => {
  const track = document.getElementById('alsoTrack');
  const nav   = document.getElementById('alsoNav');
  const frame = document.getElementById('alsoFrame');
  if (!track || !nav) return;

  const arrows = [...nav.querySelectorAll('.also__arrow')];

  function step() {
    const cards = track.children;
    const z = layoutScale();
    if (cards.length > 1) {
      return (cards[1].getBoundingClientRect().left -
              cards[0].getBoundingClientRect().left) / z;
    }
    return cards.length ? cards[0].getBoundingClientRect().width / z : track.clientWidth;
  }

  /* Nothing to scroll means nothing to steer, and an arrow at either end has
     nowhere to go. The same test drives the edge fades. */
  function sync() {
    const room = track.scrollWidth - track.clientWidth;
    const scrollable = room > 2;
    const atStart = track.scrollLeft < 2;
    const atEnd = track.scrollLeft > room - 2;

    nav.hidden = !scrollable;
    arrows[0].disabled = atStart;
    arrows[1].disabled = atEnd;

    if (frame) {
      frame.classList.toggle('can-prev', scrollable && !atStart);
      frame.classList.toggle('can-next', scrollable && !atEnd);
    }
  }

  nav.addEventListener('click', e => {
    const btn = e.target.closest('.also__arrow');
    if (btn) track.scrollLeft += step() * Number(btn.dataset.dir);
  });

  track.addEventListener('scroll', sync, { passive: true });
  addEventListener('resize', sync);
  sync();
})();

/* Recently viewed, and what it does to the suggestions (product pages)

   The server already ordered "You might also like" by how close each price is
   to the build on screen. This adds the second half: what this browser has
   looked at before. The two are blended into one target budget and the cards
   are re-ranked against it.

   The list never leaves the browser - it is a handle and a price per product,
   in localStorage, and nothing is sent anywhere. */
(() => {
  const prod = document.getElementById('prod');
  if (!prod) return;

  const KEY = 'vt-viewed';
  const CAP = 12;

  const handle = prod.dataset.handle;
  const price  = Number(prod.dataset.price) || 0;

  let history = [];
  try { history = JSON.parse(localStorage.getItem(KEY)) || []; } catch (e) {}
  if (!Array.isArray(history)) history = [];

  /* Read before writing: if this product is already in the list, its own price
     would otherwise be counted twice and drag the target onto itself. */
  const before = history.filter(v => v && v.h && v.h !== handle);

  if (handle) {
    try {
      localStorage.setItem(KEY, JSON.stringify(
        [{ h: handle, p: price }].concat(before).slice(0, CAP)
      ));
    } catch (e) {}
  }

  const track = document.getElementById('alsoTrack');
  if (!track || !before.length) return;   // nothing looked at yet: the server order stands

  const seen = new Set(before.map(v => v.h));

  /* Say so on the card. This is written here rather than in the template on
     purpose: the page itself is cached and served to everyone, so a label
     rendered on the server would show one shopper's history to the next. */
  const clock =
    '<svg class="build__tag-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<circle cx="12" cy="12" r="9"/><path d="M12 7v5.2l3.4 2"/></svg>';

  function mark(card) {
    const link = card.querySelector('.build__link');
    if (!link || link.querySelector('.build__tag--seen')) return;

    // a card only has the tag row when the product carries a badge
    let row = link.querySelector('.build__tags');
    if (!row) {
      row = document.createElement('p');
      row.className = 'build__tags';
      link.insertBefore(row, link.querySelector('.build__name'));
    }

    const label = document.createElement('span');
    label.className = 'build__tag build__tag--seen';
    label.innerHTML = clock + 'Recently viewed';
    row.appendChild(label);           // after the product's own tags
  }

  track.querySelectorAll('.build').forEach(card => {
    if (seen.has(card.dataset.handle)) mark(card);
  });

  const spent = before.map(v => Number(v.p)).filter(n => n > 0);
  if (!spent.length) return;          // labelled, but nothing to re-rank against

  const usual = spent.reduce((a, b) => a + b, 0) / spent.length;

  /* The build in front of them counts for more than the ones behind, but a
     shopper who has been reading $700 builds should not be shown the top of
     the range just because they clicked one expensive machine. */
  const target = price ? (price * 0.65) + (usual * 0.35) : usual;
  const penalty = target * 0.5;

  [...track.children]
    .map(card => {
      let score = Math.abs((Number(card.dataset.price) || 0) - target);
      // already seen it - keep it, but behind anything they have not
      if (seen.has(card.dataset.handle)) score += penalty;
      return { card: card, score: score };
    })
    .sort((a, b) => a.score - b.score)
    .forEach(entry => track.appendChild(entry.card));
})();
